import styled from 'styled-components'
import Palette from '@telus-uds/palette-allium/build/web/palette'
import { Button } from '@telus-uds/components-web' // Used for ClearButtonFull

const VIEWPORT = {
  xs: '0px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
}

// Color mapping following UDS conventions
export const hexColors = (color: string) => {
  const colors = {
    'TELUS Purple': Palette.color.purpleTelus,
    'Accessible Green': Palette.color.greenAccessible,
    White: Palette.color.white,
    'Thunder Grey': Palette.color.greyThunder,
    // Custom colors from Palette
    'Light Background': Palette.color.greyAthens,
    'Border Grey': Palette.color.greyCloud,
    'Shuttle Grey': Palette.color.greyShuttle,
    default: 'transparent',
  }

  return colors[color] ? colors[color] : colors.default
}

export const ChatbotContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 100vh;
  background-color: ${hexColors('Light Background')};
  box-sizing: border-box;
  margin-bottom: 40px;
  position: relative;
`

export const ChatSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0 auto;
  padding: 0;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`

export const ChatContent = styled.div`
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  max-width: 100%;
  /* Extra bottom padding ensures content clears the sticky InputSection (~80px tall on mobile) */
  padding: 16px 16px 120px;

  @media (min-width: ${VIEWPORT.sm}) {
    max-width: 576px;
    padding: 16px 16px 120px;
    width: 100%;
  }

  @media (min-width: ${VIEWPORT.md}) {
    max-width: 768px;
    width: 100%;
    /* Extra bottom padding ensures content clears the sticky InputSection (~130px tall on desktop) */
    padding: 32px 16px 160px;
  }

  @media (min-width: ${VIEWPORT.lg}) {
    max-width: 992px;
    width: 83.3333%;
    padding: 32px 16px 160px;
  }

  @media only screen and (min-width: ${VIEWPORT.xl}) {
    max-width: 1200px;
    width: 83.3333%;
    padding: 32px 16px 160px;
  }
`

export const DisclaimerContainer = styled.div`
  border-top: 1px solid ${hexColors('Border Grey')};
  border-bottom: 1px solid ${hexColors('Border Grey')};
`

export const InputSection = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${hexColors('Light Background')};
  display: flex;
  flex-direction: column;
  z-index: 10;
  flex-shrink: 0;
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;
  gap: 0;
  border-top: 1px solid ${hexColors('Border Grey')};
`

export const ClearButtonFull = styled(Button)`
  display: inline-flex;
`

export const ClearButtonFullWrapper = styled.div`
  display: none;
  @media (min-width: ${VIEWPORT.md}) {
    display: flex;
  }
`

export const ClearButtonIconWrapper = styled.div`
  display: flex;
  @media (min-width: ${VIEWPORT.md}) {
    display: none;
  }
`

export const SearchMobileWrapper = styled.div`
  display: block;
  flex-grow: 1;
  @media (min-width: ${VIEWPORT.lg}) {
    display: none;
  }
`

export const SearchDesktopWrapper = styled.div`
  display: none;
  flex-grow: 1;
  @media (min-width: ${VIEWPORT.lg}) {
    display: block;
  }
`

export const InputContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 16px 16px;
  box-sizing: border-box;

  @media (min-width: ${VIEWPORT.md}) {
    padding: 32px 0 48px;
  }
`

export const InputContentInnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 auto;

  @media (min-width: 0px) and (max-width: ${VIEWPORT.sm}) {
    max-width: 100%;
  }

  @media (min-width: ${VIEWPORT.sm}) and (max-width: ${VIEWPORT.md}) {
    max-width: 576px;
  }

  @media (min-width: ${VIEWPORT.md}) and (max-width: ${VIEWPORT.lg}) {
    max-width: 768px;
  }

  @media (min-width: ${VIEWPORT.lg}) and (max-width: ${VIEWPORT.xl}) {
    max-width: 992px;
  }

  @media only screen and (min-width: ${VIEWPORT.xl}) {
    max-width: 1200px;
  }
`

export const MessageTagWrapper = styled.div<{ messageType: 'Human' | 'Assistant' }>`
  display: flex;
  font-size: 16px;
  font-family: 'HNforTELUSSA500normal';
  margin-bottom: 8px;
  /* Icon wrapper is 56px wide (including padding). Negative margin aligns the tag icon
     next to the header text by compensating for the wrapper's padding and positioning. */
  margin-left: ${(props) => (props.messageType === 'Human' ? 'auto' : '-56px')};
  margin-right: ${(props) => (props.messageType === 'Human' ? '-56px' : 'auto')};
  padding-left: 16px;
  padding-right: 16px;
  align-items: center;
  gap: 8px;
  flex-direction: ${(props) => (props.messageType === 'Human' ? 'row-reverse' : 'row')};

  @media (min-width: 0px) and (max-width: ${VIEWPORT.lg}) {
    padding-left: 0;
    padding-right: 0;
    margin-left: auto;
    margin-right: auto;
  }
`

export const MessageTagIconWrapper = styled.span`
  display: none;

  @media (min-width: ${VIEWPORT.lg}) {
    display: inline;
  }
`

export const MessageBubble = styled.div<{ messageType: 'Human' | 'Assistant' }>`
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${(props) => (props.messageType === 'Human' ? hexColors('TELUS Purple') : hexColors('White'))};
  color: ${(props) => (props.messageType === 'Human' ? hexColors('White') : hexColors('Thunder Grey'))};
  max-width: 80%;
  width: fit-content;
  margin-left: ${(props) => (props.messageType === 'Human' ? 'auto' : '0')};
  margin-right: ${(props) => (props.messageType === 'Human' ? '0' : 'auto')};
  margin-bottom: 16px;
  font-size: 16px;
  line-height: 24px;
  word-break: break-word;
`

export const SourcesContainer = styled.div`
  margin-top: 8px;
  font-size: 12px;
`

export const SourceItem = styled.div`
  margin-top: 4px;
`

export const ExampleQuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

export const LoadingIndicatorWrapper = styled.div`
  text-align: center;
  padding: 64px 0 8px;
`

export const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  padding-top: 12px;
`

export const FeedbackTextSubmitted = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: ${hexColors('Accessible Green')};
`

export const FeedbackText = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: ${hexColors('Shuttle Grey')};
`

export const FeedbackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  justify-content: space-between;
`

export const FeedbackActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

export const FeedbackThumbDownOffset = styled.div`
  display: flex;
  /* Offset the thumbs-down icon to align the center of the thumbs-up icon with the fingers */
  transform: translateY(8px);
`

export const FeedbackCommentContainer = styled.div`
  flex-basis: 100%;
  width: 100%;
  margin-top: 8px;
`

export const FeedbackCommentField = styled.div`
  width: 100%;
`

export const FeedbackCommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 32px;
`

export const TableScrollWrapper = styled.div`
  overflow-x: auto;
  margin: 12px 0;
  max-width: 100%;

  /* Only show the scrollbar when the table overflows its container.
     -webkit-appearance: none opts Safari out of its native overlay scrollbar so the
     custom pseudo-element styles below are actually applied. */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${hexColors('Light Background')};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${hexColors('Border Grey')};
    border-radius: 3px;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${hexColors('Border Grey')} ${hexColors('Light Background')};
`

export const TableMinWidthWrapper = styled.div`
  min-width: max-content;
`

export const TableCellContent = styled.span`
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
`
